import { reflect } from '@effector/reflect';
import { Button, TextInput } from '@mantine/core';
import { combine } from 'effector';
import { useUnit } from 'effector-react';
import { not } from 'patronum';
import { createBlockCashdeskModal } from 'features/block-cashdesk';
import { createDeleteCashdeskModal } from 'features/delete-cashdesk';
import { createDepositCashdeskModal } from 'features/deposit-cashdesk';
import { createUnblockCashdeskModal } from 'features/unblock-cashdesk';
import { createWithdrawCashdeskModal } from 'features/withdraw-cashdesk';
import {
	CASHDESK_NAME_MAX_LENGTH,
	CASHDESK_NAME_MIN_LENGTH,
	CASHDESK_NOTE_MAX_LENGTH,
	CASHDESK_FAST_DEPOSITS_MAX_LENGTH,
} from 'entities/cashdesk';
import { viewerModel } from 'entities/viewer';
import { translated, $t } from 'shared/lib';
import { MultiSelect, TagsInputNumeric } from 'shared/ui';
import { cashdeskInfoModel, cashdeskPageModel } from '../../model';
import cls from './cashdesk-page-form.module.scss';

const DepositCashdeskModal = createDepositCashdeskModal(cashdeskInfoModel.depositCashdeskModel);

const WithdrawCashdeskModal = createWithdrawCashdeskModal(cashdeskInfoModel.withdrawCashdeskModel);

const BlockCashdeskModal = createBlockCashdeskModal(cashdeskInfoModel.blockCashdeskModel);

const UnblockCashdeskModal = createUnblockCashdeskModal(cashdeskInfoModel.unblockCashdeskModel);

const DeleteCashdeskModal = createDeleteCashdeskModal(cashdeskInfoModel.deleteCashdeskModel);

const NameField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskInfoModel.form.fields.name.$value,
		onChange: (e) => cashdeskInfoModel.form.fields.name.change(e.target.value),
		label: translated('cashdesk-page.name'),
		placeholder: translated('cashdesk-page.name'),
		errorProps: { 'data-test-id': 'cashdesk-name-input-error' },
		error: combine(
			[cashdeskInfoModel.form.fields.name.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('add-cashdesk.name'),
					maxLength: CASHDESK_NAME_MAX_LENGTH,
					minLength: CASHDESK_NAME_MIN_LENGTH,
				}),
		),
		readOnly: not(viewerModel.$canWriteCashdesks),
		required: true,
	},
});

const AccountField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskInfoModel.form.fields.account.$value,
		label: translated('cashdesk-page.account'),
		readOnly: true,
	},
});

const CurrencyField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskInfoModel.form.fields.currency.$value,
		label: translated('cashdesk-page.currency'),
		readOnly: true,
	},
});

const BalanceField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskInfoModel.form.fields.balance.$value,
		label: translated('cashdesk-page.balance'),
		readOnly: true,
	},
});

const CashiersField = reflect({
	view: MultiSelect,
	bind: {
		'data-test-id': 'cashdesk-page-cashiers-select',
		value: cashdeskInfoModel.form.fields.cashierIds.$value.map((ids) => ids.map(String)),
		onChange: (value) => cashdeskInfoModel.form.fields.cashierIds.change(value.map(Number)),
		label: translated('cashdesk-page.cashiers'),
		placeholder: translated('cashdesk-page.cashiers'),
		nothingFoundMessage: translated('cashdesk-page.cashiers-not-found'),
		readOnly: not(viewerModel.$canWriteCashdesks),
		data: cashdeskPageModel.$cashiers,
		fullWidth: true,
	},
});

const FastDepositsField = reflect({
	view: TagsInputNumeric,
	bind: {
		'data-test-id': 'cashdesk-page-fast-deposits-input',
		value: cashdeskInfoModel.form.fields.fastDeposits.$value.map((values) => values.map(String)),
		onChange: (values) => cashdeskInfoModel.form.fields.fastDeposits.change(values.map(Number)),
		label: translated('cashdesk-page.fast-deposits'),
		placeholder: translated('cashdesk-page.fast-deposits-placeholder'),
		fullWidth: true,
		readOnly: not(viewerModel.$canWriteCashdesks),
		error: combine(
			[cashdeskInfoModel.form.fields.fastDeposits.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('cashdesk-page.fast-deposits'),
					maxLength: CASHDESK_FAST_DEPOSITS_MAX_LENGTH,
				}),
		),
	},
});

const NoteField = reflect({
	view: TextInput,
	bind: {
		value: cashdeskInfoModel.form.fields.note.$value,
		onChange: (e) => cashdeskInfoModel.form.fields.note.change(e.target.value),
		label: translated('cashdesk-page.note'),
		placeholder: translated('cashdesk-page.note'),
		readOnly: not(viewerModel.$canWriteCashdesks),
		errorProps: { 'data-test-id': 'cashdesk-note-input-error' },
		error: combine(
			[cashdeskInfoModel.form.fields.note.$errors, $t],
			([errors, t]) =>
				errors.length > 0 &&
				t(`errors:validation.${errors[0]}`, {
					fieldName: t('add-cashdesk.note'),
					maxLength: CASHDESK_NOTE_MAX_LENGTH,
				}),
		),
	},
});

const SaveButton = reflect({
	view: Button,
	bind: {
		type: 'submit',
		disabled: cashdeskInfoModel.form.$pristine,
		children: translated('cashdesk-page.save'),
		loading: cashdeskInfoModel.editCashdeskMutation.$pending,
		className: cls.cashdeskPageFormFooterOption,
	},
});

export const CashdeskPageForm = () => {
	const [cashdesk, submit] = useUnit([cashdeskPageModel.$cashdesk, cashdeskInfoModel.form.submit]);

	return (
		cashdesk !== null && (
			<>
				<form
					className={cls.cashdeskPageForm}
					onSubmit={(event) => {
						event.preventDefault();
						submit();
					}}
				>
					<NameField />
					<AccountField />
					<CurrencyField />
					<div className={cls.cashdeskPageFormBalance}>
						<BalanceField />
						<div className={cls.cashdeskPageFormBalanceOptions}>
							<DepositCashdeskModal.Button
								modalData={cashdesk}
								className={cls.cashdeskPageFormBalanceOption}
							/>
							<WithdrawCashdeskModal.Button
								modalData={cashdesk}
								className={cls.cashdeskPageFormBalanceOption}
							/>
						</div>
					</div>
					<CashiersField />
					<FastDepositsField />
					<NoteField />
					<div className={cls.cashdeskPageFormFooter}>
						<SaveButton />
						<div className={cls.cashdeskPageFormFooterOptions}>
							{cashdesk.isLocked ? (
								<UnblockCashdeskModal.Button
									modalData={cashdesk}
									className={cls.cashdeskPageFormFooterOption}
								/>
							) : (
								<BlockCashdeskModal.Button
									modalData={cashdesk}
									className={cls.cashdeskPageFormFooterOption}
								/>
							)}
							<DeleteCashdeskModal.Button
								modalData={cashdesk}
								className={cls.cashdeskPageFormFooterOption}
							/>
						</div>
					</div>
				</form>
				<DepositCashdeskModal />
				<WithdrawCashdeskModal />
				<BlockCashdeskModal />
				<UnblockCashdeskModal />
				<DeleteCashdeskModal />
			</>
		)
	);
};
